/**
 * TicketedEventOrder Entity Schemas
 *
 * An Order is a multi-line-item RSVP placed against a TicketedEvent that has
 * one or more TicketTypes. Each Order has one or more OrderItems, each
 * referencing a TicketType with a snapshotted name and price.
 */

import { z } from 'zod'
import { TicketedEventAdmissionPublicSchema, TicketedEventAdmissionSchema } from './ticketed-event-admission'
import {
  BuyerCancellabilitySchema,
  BuyerContactSchema,
  BuyerRefundStatusSchema,
  BuyerSelfServiceSchema
} from './ticketed-event-buyer-self-service'
import { TicketedEventFormCompletionTimingSchema } from './ticketed-event-form-requirement'

// `awaiting_payment` (U6): a paid order whose seat is held but whose charge is
// not yet captured — the Stripe webhook capture flips it to `confirmed`, or the
// expiry sweep releases it. Kept last so this stays an additive change.
export const TicketedEventOrderStatusSchema = z.enum([
  'confirmed',
  'pending',
  'rejected',
  'cancelled',
  'awaiting_payment'
])
export type TicketedEventOrderStatus = z.infer<typeof TicketedEventOrderStatusSchema>

// ============================================
// Attached-form completion (events↔forms)
// ============================================

/**
 * Per-requirement completion status for an order (U6). Mirrors the backend
 * `completion-status.ts` derivation. `readyToAttend === false` while
 * `status === 'confirmed'` is the derived "confirmed-but-incomplete" state.
 */
export const RequirementCompletionStatusSchema = z.object({
  requirementId: z.string(),
  label: z.string(),
  required: z.boolean(),
  completionTiming: TicketedEventFormCompletionTimingSchema,
  scope: z.enum(['order', 'attendee']),
  attendeePublicId: z.string().nullable(),
  satisfied: z.boolean()
})
export type RequirementCompletionStatus = z.infer<typeof RequirementCompletionStatusSchema>

export const CompletionSummarySchema = z.object({
  requirements: z.array(RequirementCompletionStatusSchema),
  readyToAttend: z.boolean(),
  outstandingRequiredCount: z.number().int()
})
export type CompletionSummary = z.infer<typeof CompletionSummarySchema>

/** One attached-form answer field, rendered from the submission snapshot (U7). */
export const FormAnswerFieldSchema = z.object({
  fieldId: z.string(),
  label: z.string(),
  value: z.string()
})
export type FormAnswerField = z.infer<typeof FormAnswerFieldSchema>

/** One completed attached form bound to an order (U7). */
export const AttachedFormAnswerSetSchema = z.object({
  requirementId: z.string(),
  requirementLabel: z.string(),
  attendeePublicId: z.string().nullable(),
  formTitle: z.string(),
  fields: z.array(FormAnswerFieldSchema)
})
export type AttachedFormAnswerSet = z.infer<typeof AttachedFormAnswerSetSchema>

/**
 * One outstanding before-attendance requirement in the registrant-facing
 * (public order) shape (U9). Minimal — only what a guest needs to find +
 * complete the form; no admin-only fields.
 */
export const PublicOutstandingRequirementSchema = z.object({
  requirementId: z.string(),
  label: z.string(),
  formPublicSlug: z.string().nullable(),
  attendeePublicId: z.string().nullable(),
  attendeeName: z.string().nullable()
})
export type PublicOutstandingRequirement = z.infer<typeof PublicOutstandingRequirementSchema>

export const TicketedEventCustomQuestionTypeSchema = z.enum([
  'short_text',
  'long_text',
  'single_select',
  'multi_select',
  'checkbox'
])
export type TicketedEventCustomQuestionType = z.infer<typeof TicketedEventCustomQuestionTypeSchema>

export const TicketedEventOrderAnswerSchema = z.object({
  questionId: z.string(),
  prompt: z.string(),
  type: TicketedEventCustomQuestionTypeSchema,
  value: z.union([z.string(), z.array(z.string()), z.boolean()]).nullable()
})
export type TicketedEventOrderAnswer = z.infer<typeof TicketedEventOrderAnswerSchema>

export const TicketedEventOrderItemSchema = z.object({
  id: z.string(),
  ticketTypeId: z.string(),
  ticketTypeName: z.string(),
  ticketTypePriceCents: z.number().int().nullable(),
  quantity: z.number().int(),
  // The all-seats rollup, not a seat's own stamp. The field is NOT new: it was
  // read straight off the `checked_in_at` column on `TicketedEventOrderItem`
  // until the P3 backend half (U4, SCH-1622).
  //
  // Since P3 it is DERIVED from the item's live admissions
  // (`toTicketedEventOrderItemDto` in
  // `apps/backend/src/services/ticketed-events/shared/mappers.ts`): non-null
  // only when EVERY live admission on this item is checked in, and then the
  // LATEST of those stamps (KTD6). A partly-scanned item reads null here, so a
  // shipped client that only knows this field keeps its "the whole line is in"
  // meaning (R46). Partial state is visible only through `admissions` below.
  //
  // The item column still exists and is still what people WRITE until U8: the
  // admin toggles `toggleOrderItemCheckIn` and `toggleOrderCheckIn`
  // (`apps/backend/src/services/ticketed-events/orders.ts`) and the door's
  // `markOrderCheckIn` stamp the item and mirror the stamp onto its seats in
  // the same transaction (SCH-1758), so the column and the derivation agree.
  //
  // There is no order-level `checkedInAt` in this file — not on
  // `TicketedEventOrderSchema`, not on the summary row, not on the public
  // order. The order-grain stamp is the door's order row,
  // `PublicCheckInAttendeeSchema.checkedInAt` in
  // `packages/shared-types/src/api/ticketed-events/check-in-links.ts`, and P3
  // gave it the SAME derivation as this field: the latest stamp, non-null only
  // when every live admission on the order is checked in, where it had been the
  // earliest item stamp. KTD6 names that field outright. R46 did not hold it
  // back — R46 is scoped to fields a shipped MOBILE build reads from the order
  // payload, and the door row is consumed by a web surface
  // (`apps/web/src/app/(public)/check-in/[school]/[token]/public-check-in-client.tsx`)
  // that redeploys on every merge, so it carries no installed-build risk. U8
  // adds the per-admission door rows on top; it does not establish this
  // meaning.
  checkedInAt: z.string().nullable(),
  // The seats behind `quantity`, one entry each, so a client can act on or
  // render a single seat (R21). Optional and additive: declared here before any
  // backend emits it, so an installed build parses the restructured payload
  // unchanged (AE15) — and so producers that have not been taught to expand
  // seats yet keep satisfying this shape. Absent and `undefined` both parse.
  //
  // The FULL, holder-bearing seat shape, unlike the my-tickets row and the
  // public order below: this item shape is reached only from
  // `TicketedEventOrderSchema`, which is the authenticated admin order and is
  // referenced by no unauthenticated payload. If that ever changes, this array
  // takes `TicketedEventAdmissionPublicSchema` too.
  admissions: z.array(TicketedEventAdmissionSchema).optional()
})
export type TicketedEventOrderItem = z.infer<typeof TicketedEventOrderItemSchema>

export const TicketedEventOrderSchema = z.object({
  id: z.string(),
  ticketedEventId: z.string(),
  referenceCode: z.string(),
  buyerEmail: z.string(),
  buyerFirstName: z.string(),
  buyerLastName: z.string(),
  // The purchase snapshot: how many seats were bought. Never decremented when a
  // seat is cancelled or voided, because shipped mobile builds read it (R46).
  totalQuantity: z.number().int(),
  // How many seats on the order are still live (`active` or `deactivated`) —
  // what admin seat totals count (SCH-2507). Zero for an order whose seats were
  // all removed, and for a pending application, which holds none yet. Optional
  // and additive: emitted only when the order is read with its items, and
  // absent on older cached payloads.
  liveQuantity: z.number().int().optional(),
  status: TicketedEventOrderStatusSchema,
  items: z.array(TicketedEventOrderItemSchema),
  answers: z.array(TicketedEventOrderAnswerSchema),
  // Derived per-requirement completion (U6) + attached-form answers (U7).
  // Optional so non-completion code paths / cached payloads stay valid.
  completion: CompletionSummarySchema.optional(),
  formAnswers: z.array(AttachedFormAnswerSetSchema).optional(),
  // Every seat on the order, flattened across `items` — the same admissions the
  // items carry, in one list, so a door or detail surface can page through
  // seats without walking the item tree. Optional for the same reason as the
  // per-item array: it is declared ahead of the backend that emits it (KTD6).
  admissions: z.array(TicketedEventAdmissionSchema).optional(),
  createdAt: z.string(),
  updatedAt: z.string().nullable()
})
export type TicketedEventOrder = z.infer<typeof TicketedEventOrderSchema>

// Lean cross-event order row for the school-wide Order Management list
// (GET /v1/ticketed-events/orders). Deliberately omits the heavy per-order
// joins (items, answers, completion, formAnswers) — the list only needs enough
// to identify an order and route to its per-event detail view. Each row carries
// minimal parent-event context so the table can show + facet by event.
export const TicketedEventOrderEventContextSchema = z.object({
  id: z.string(),
  title: z.string(),
  startsAt: z.string(),
  // Where the event is (my-tickets R8), mirroring the field the public order
  // schema already declares below. Nullable AND optional on purpose: nullable
  // because an event need not have a location, optional because the admin
  // order-list producer (`listSchoolOrders`) does not select or emit it and
  // must keep satisfying this shape. Do not tighten to nullable-only without
  // teaching that producer to supply it.
  locationText: z.string().nullable().optional(),
  // Rich mobile My Tickets presentation fields. Optional so older producers and
  // admin order-list rows that reuse this summary shape keep parsing.
  coverImageUrl: z.string().nullable().optional(),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  locationDisplayName: z.string().nullable().optional()
})
export type TicketedEventOrderEventContext = z.infer<typeof TicketedEventOrderEventContextSchema>

export const TicketedEventOrderSummarySchema = z.object({
  id: z.string(),
  referenceCode: z.string(),
  buyerEmail: z.string(),
  buyerFirstName: z.string(),
  buyerLastName: z.string(),
  totalQuantity: z.number().int(),
  // Live seats on the order, as on `TicketedEventOrderSchema.liveQuantity`.
  // Optional: the admin order-list producer (`listSchoolOrders`) emits it; the
  // my-tickets producer does not, and `totalQuantity` keeps its meaning for the
  // mobile builds that read this row.
  liveQuantity: z.number().int().optional(),
  status: TicketedEventOrderStatusSchema,
  // The my-tickets row's seats (KTD6). This shape carries no `items`, so the
  // seats sit flat on the row — which is why each seat names its own order item
  // and tier. Optional: the admin order-list producer (`listSchoolOrders`)
  // reuses this summary and does not select them, and the field is declared
  // before any producer emits it, so today's payload — and any older cached one
  // — still parses (AE15).
  //
  // The REDUCED, email-free seat shape on purpose: the my-tickets link is an
  // unexpiring bearer credential with no auth middleware, and the backend parses
  // its response through this very schema, which makes this declaration an
  // outbound field whitelist. See `TicketedEventAdmissionPublicSchema`.
  admissions: z.array(TicketedEventAdmissionPublicSchema).optional(),
  // Whether the buyer can cancel this order or request a refund from the row
  // (U14 — R32, R33, R34). Optional: the my-tickets producer emits it, the
  // admin order-list producer does not, and an installed mobile build that
  // predates it parses the row unchanged (AE15).
  selfService: BuyerSelfServiceSchema.optional(),
  createdAt: z.string(),
  event: TicketedEventOrderEventContextSchema
})
export type TicketedEventOrderSummary = z.infer<typeof TicketedEventOrderSummarySchema>

// Registrant-facing public order (GET /by-slug/:school/orders/:ref). Mirrors
// the backend PublicOrderDto wire shape — the handler returns this under a
// top-level `{ data }` envelope (see TicketedEventOrderPublicResponseSchema).
export const TicketedEventOrderPublicSchema = z.object({
  referenceCode: z.string(),
  totalQuantity: z.number().int(),
  buyerEmail: z.string(),
  buyerFirstName: z.string(),
  buyerLastName: z.string(),
  status: TicketedEventOrderStatusSchema,
  items: z.array(
    z.object({
      ticketTypeName: z.string(),
      quantity: z.number().int()
    })
  ),
  // The buyer's own seats, flat on the order rather than nested under `items`:
  // the item rows here are anonymous (`{ticketTypeName, quantity}`, no id), so
  // there is nothing to hang a per-seat list off, and a confirmation surface
  // renders one list of tickets rather than a list per tier.
  //
  // Optional and additive on the same terms as the array on the admin order and
  // the my-tickets row — declared here BEFORE anything emits it, because Zod
  // strips undeclared keys and a shipped build would otherwise throw the seats
  // away silently (R46/AE15). U17 (SCH-1635) taught `get-public-order.ts` to
  // emit it, through the flat public admission select and an outbound parse of
  // the array through `TicketedEventAdmissionPublicSchema`; the mobile ticket
  // screen renders it. An order whose seats are not yet backfilled sends `[]`.
  //
  // Naming only (KTD1): the field says `admissions`; the copy rendered from it
  // says "ticket" or "registration", never "admission".
  //
  // The REDUCED, email-free seat shape: this order is read from a forwarded
  // reference-code link with no authentication, so it must not carry the email
  // addresses of the other people on the order. See
  // `TicketedEventAdmissionPublicSchema`.
  admissions: z.array(TicketedEventAdmissionPublicSchema).optional(),
  event: z.object({
    id: z.string(),
    title: z.string(),
    startsAt: z.string(),
    locationText: z.string().nullable(),
    coverImageUrl: z.string().nullable().optional(),
    latitude: z.number().nullable().optional(),
    longitude: z.number().nullable().optional(),
    locationDisplayName: z.string().nullable().optional(),
    kind: z.enum(['ticketed', 'signup', 'application']),
    confirmationMessage: z.string().nullable()
  }),
  outstandingRequirements: z.array(PublicOutstandingRequirementSchema),
  // Buyer self-service (U14 — R32, R33, R34, AE9). All optional and additive,
  // so an installed build that predates them parses this payload unchanged
  // (R46/AE15). `liveQuantity` is the seats still held (`active` or
  // `deactivated`); `totalQuantity` above stays the purchase snapshot, so a
  // surface shows the live count with the removed seats struck through.
  // `refundStatus` folds every refund row on the order; the admin's note and the
  // payment link never reach this unauthenticated payload.
  liveQuantity: z.number().int().optional(),
  cancellable: BuyerCancellabilitySchema.optional(),
  refundStatus: BuyerRefundStatusSchema.optional(),
  refundRequestable: z.boolean().optional(),
  contact: BuyerContactSchema.optional(),
  // Wallet pass links for a confirmed order (plan 2026-09-01-1254, R1/R9).
  // Optional AND per-platform nullable, and the two mean different things:
  // the field is absent when this deployment issues no passes at all (no
  // public origin, no link secret) or the order is not confirmed, while a
  // null inside it means that one platform has no credential filed. Producers
  // predating wallet keep satisfying this shape.
  wallet: z
    .object({
      apple: z.string().url().nullable(),
      google: z.string().url().nullable()
    })
    .optional()
})
export type TicketedEventOrderPublic = z.infer<typeof TicketedEventOrderPublicSchema>

/** The `{ data }`-wrapped wire envelope the public order endpoint returns. */
export const TicketedEventOrderPublicResponseSchema = z.object({
  data: TicketedEventOrderPublicSchema
})
export type TicketedEventOrderPublicResponse = z.infer<typeof TicketedEventOrderPublicResponseSchema>
